import useResizeObserver from 'resize-observer-hook';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { GeoChartMeta, GeoOptions } from '../types';
import {
    COORD_WIDTH,
    DEFAULT_COLUMN_WIDTH,
    DEFAULT_MAX_X,
    DEFAULT_MIN_X,
    DEFAULT_SCALE,
    DEPTH_COLUMN_WIDTH,
    DEPTH_STEP,
    DISTANCE_STEP,
    metaContext,
    PERFORATION_WIDTH,
    TESTING_WIDTH,
    WELL_WIDTH,
} from '../base';
import { getColumns } from '../BodyColumns/BodyColumns';
import { GeoChartBase } from './GeoChartBase';
import type { TestingData } from '@/api/api.v2';
import { getMinMaxDepth, getSelectedEntities, useGeoData } from '../utils';
import type { CommonPerfType, GeoData } from '@/store/geo-test/types';
import { addLevelsToPerforations, addLevelsToTests } from './GeoChartBody/GeoChartBodyPicture';
import { max } from 'lodash';

type GeoChartProps = {
    title?: string;
    options?: GeoOptions;
};

const getScrollbarSize = (): number => {
    const scrollDiv = document.createElement('div');
    scrollDiv.style.width = '100px';
    scrollDiv.style.height = '100px';
    scrollDiv.style.overflow = 'scroll';
    scrollDiv.style.position = 'absolute';
    scrollDiv.style.top = '-9999px';

    document.body.appendChild(scrollDiv);
    const scrollbarHeight = scrollDiv.offsetHeight - scrollDiv.clientHeight;
    document.body.removeChild(scrollDiv);

    return scrollbarHeight;
};

const getWidth = (containerWidth: number, minWidth?: number) => {
    if (minWidth && containerWidth < minWidth) {
        return minWidth;
    }
    return containerWidth;
};

export const PERF_OFFSET = 5;
export const TEST_OFFSET = 10;

const calculateMaxOverlapScore = (
    geoData: GeoData,
    testingData: Record<number, TestingData[]>,
    perforationData: Record<number, CommonPerfType[]>,
    pictureWidth: number,
): [number, number] => {
    const result: Record<number, number> = {};
    if (!(geoData.wells && geoData.wells.length !== 0)) {
        return [0, 0];
    }

    for (const well of geoData.wells) {
        const wellId = well.wellId;
        const tests = testingData[wellId] ?? [];
        const perfs = perforationData[wellId] ?? [];

        const testsWithLevels = addLevelsToTests(tests);
        const perfsWithLevel = addLevelsToPerforations(perfs, testsWithLevels);

        const perfCount = max(perfsWithLevel.map(({ level }) => level + 1)) ?? 0;
        const testCount = tests.length > 0 ? 1 : 0;

        const maxScore = TESTING_WIDTH * testCount + PERFORATION_WIDTH * perfCount;

        result[wellId] = maxScore + COORD_WIDTH;
    }

    const firstLen = geoData.wells[0].x;
    const totalLen = geoData.wells.at(-1)!.x - firstLen;

    let maxScale = 1;

    for (let i = 0; i < geoData.wells.length - 1; i++) {
        const well = geoData.wells[i];
        const availablePixels = (pictureWidth * (geoData.wells[i + 1].x - well.x)) / totalLen;

        if (result[well.wellId] > availablePixels && result[well.wellId] / availablePixels > maxScale) {
            maxScale = result[well.wellId] / availablePixels;
        }
    }

    return [maxScale === 1 ? 0 : pictureWidth * maxScale, result[geoData.wells.at(-1)!.wellId]];
};

export const GeoChart: FC<GeoChartProps> = ({ title, options }) => {
    const { geoData, testingData, perforationData, selectedPerforations, selectedTestings } = useGeoData();

    const [containerRef, containerWidth, containerHeight] = useResizeObserver<HTMLDivElement>();

    const pictureWidth = containerWidth - (5 * DEFAULT_COLUMN_WIDTH + DEPTH_COLUMN_WIDTH + 19);

    const sizeByOverlap = useMemo(
        () =>
            calculateMaxOverlapScore(
                geoData,
                testingData,
                getSelectedEntities(perforationData, selectedPerforations),
                pictureWidth - 240,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pictureWidth, geoData.wells.map(({ wellId }) => wellId).join(':'), selectedPerforations, selectedTestings],
    );

    const [widthL, endL] = sizeByOverlap;
    const minWidth = widthL === 0 ? 0 : widthL + endL;

    const isReady = containerRef !== undefined && containerWidth !== undefined && containerHeight !== undefined;

    const [scrollbarHeight, setScrollbarHeight] = useState(0);
    const [meta, setMeta] = useState<GeoChartMeta | null>(null);

    useEffect(() => {
        setScrollbarHeight(getScrollbarSize());
    }, []);

    useEffect(() => {
        if (isReady && geoData) {
            const scaleMetersToPixelsY = (meters: number) => {
                return meters / (options?.verticalScale ?? DEFAULT_SCALE);
            };

            const [minDepth, maxDepth] = getMinMaxDepth(geoData);

            const firstStepDepth = Math.floor(minDepth / DEPTH_STEP) * DEPTH_STEP;
            const lastStepDepth = Math.ceil(maxDepth / DEPTH_STEP) * DEPTH_STEP;
            const totalDepth = lastStepDepth - firstStepDepth;
            const contentHeight = scaleMetersToPixelsY(totalDepth);

            let minX: number | undefined = undefined;
            let maxX: number | undefined = undefined;

            const processX = (x: number) => {
                minX = minX !== undefined ? Math.min(minX, x) : x;
                maxX = maxX !== undefined ? Math.max(maxX, x) : x;
            };

            for (const well of geoData.wells) {
                processX(well.x);
            }

            if (minX === undefined || maxX === undefined) {
                minX = DEFAULT_MIN_X;
                maxX = DEFAULT_MAX_X;
            }

            const newMeta: GeoChartMeta = {
                container: {
                    width: containerWidth,
                    height: containerHeight,
                },
                content: {
                    height: contentHeight,
                },
                columns: getColumns(),
                depths: {
                    first: firstStepDepth,
                    last: lastStepDepth,
                    total: totalDepth,
                    depthToPixelsY: (depth: number) => scaleMetersToPixelsY(depth - firstStepDepth),
                    metersToPixelsY: scaleMetersToPixelsY,
                },
                picture: undefined,
            };

            if (containerWidth !== undefined) {
                const calculatedWidth = getWidth(pictureWidth, minWidth);
                const firstStepXBase = Math.floor(minX / DISTANCE_STEP) * DISTANCE_STEP;
                const lastStepXBase = Math.ceil(maxX / DISTANCE_STEP) * DISTANCE_STEP;
                const totalDistanceBase = lastStepXBase - firstStepXBase;

                const scalePixelsToBaseMetersX = (pixels: number) => {
                    return (pixels / calculatedWidth) * totalDistanceBase;
                };

                minX = minX - scalePixelsToBaseMetersX(WELL_WIDTH * 3);
                maxX = maxX + scalePixelsToBaseMetersX((WELL_WIDTH * (endL - COORD_WIDTH + 16)) / 20);

                const firstStepX = Math.floor(minX / DISTANCE_STEP) * DISTANCE_STEP;
                const lastStepX = Math.ceil(maxX / DISTANCE_STEP) * DISTANCE_STEP;

                const totalDistance = lastStepX - firstStepX;

                const scaleMetersToPixelsX = (meters: number) => {
                    return (meters / totalDistance) * calculatedWidth;
                };

                newMeta.picture = {
                    width: calculatedWidth,
                    firstX: firstStepX,
                    lastX: lastStepX,
                    endX: scalePixelsToBaseMetersX((WELL_WIDTH * (endL - COORD_WIDTH + 16)) / 20),
                    distanceToPixelsX: (x: number) => scaleMetersToPixelsX(x - firstStepX),
                    metersToPixelsX: scaleMetersToPixelsX,
                };
            }

            setMeta(newMeta);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        containerHeight,
        containerWidth,
        isReady,
        title,
        options?.verticalScale,
        minWidth,
        pictureWidth,
        endL,
        selectedPerforations,
        selectedTestings,
    ]);

    return (
        <metaContext.Provider value={meta}>
            <div ref={containerRef} className="h-full w-full p-2">
                <div
                    className="overflow-y-hidden overflow-x-auto [scrollbar-gutter:stable]"
                    style={{ height: meta ? meta.container.height - (scrollbarHeight + 64) : undefined }}
                >
                    {isReady && meta ? <GeoChartBase /> : null}
                </div>
            </div>
        </metaContext.Provider>
    );
};
